import {
    TagIcon,
    ShieldCheckIcon,
    PhoneIcon,
    CalendarDaysIcon,
} from "@heroicons/react/24/outline";

export default function BookingBenefits() {
    const benefits = [
        {
            icon: TagIcon,
            number: "01",
            title: "Best Price Guarantee",
            description: "Exclusive hotel rates with great value for your stay.",
        },
        {
            icon: ShieldCheckIcon,
            number: "02",
            title: "Secure Booking",
            description: "Your personal information and booking details stay protected.",
        },
        {
            icon: PhoneIcon,
            number: "03",
            title: "24/7 Guest Support",
            description: "Our team is available whenever you need assistance.",
        },
        {
            icon: CalendarDaysIcon,
            number: "04",
            title: "Flexible Cancellation",
            description: "Enjoy flexible options designed around your travel plans.",
        },
    ];

    return (
        <section
            className="
                relative
                w-full
                overflow-hidden
                bg-[#F3F8FF]

                px-5
                py-16

                sm:px-8
                sm:py-20

                lg:px-12
                lg:py-24
            "
        >

            {/* =====================================================
                BACKGROUND DECORATION
            ===================================================== */}

            <div
                className="
                    pointer-events-none
                    absolute
                    -left-32
                    top-10
                    h-72
                    w-72
                    rounded-full
                    bg-[#D7A74A]/10
                    blur-3xl
                "
            />

            <div
                className="
                    pointer-events-none
                    absolute
                    -right-32
                    bottom-0
                    h-80
                    w-80
                    rounded-full
                    bg-[#8B0000]/5
                    blur-3xl
                "
            />

            <div className="relative mx-auto w-full max-w-[1310px]">

                {/* =====================================================
                    INTRO
                ===================================================== */}

                <div
                    className="
                        mb-10
                        flex
                        flex-col
                        gap-5

                        lg:mb-14
                        lg:flex-row
                        lg:items-end
                        lg:justify-between
                    "
                >

                    {/* LEFT */}

                    <div className="max-w-[620px]">

                        {/* Eyebrow */}

                        <div
                            className="
                                mb-4
                                flex
                                items-center
                                gap-3
                            "
                        >
                            <span
                                className="
                                    h-px
                                    w-9
                                    bg-[#D7A74A]
                                "
                            />

                            <span
                                className="
                                    text-[10px]
                                    font-bold
                                    uppercase
                                    tracking-[0.22em]
                                    text-[#8B0000]
                                "
                            >
                                Why Book With Us
                            </span>
                        </div>

                        {/* Heading */}

                        <h2
                            className="
                                font-serif
                                text-[30px]
                                font-medium
                                leading-[1.12]
                                tracking-[-0.025em]
                                text-[#0B5552]

                                sm:text-[36px]

                                lg:text-[44px]
                            "
                        >
                            Everything you need for a
                            <span className="italic text-[#8B0000]">
                                {" "}better stay.
                            </span>
                        </h2>

                        {/* Description */}

                        <p
                            className="
                                mt-4
                                max-w-[570px]
                                text-[13px]
                                leading-6
                                text-[#71808A]

                                sm:text-[14px]
                                sm:leading-7
                            "
                        >
                            From competitive hotel rates to dedicated guest
                            support, we make booking your Dubai getaway
                            simple, secure and stress-free.
                        </p>
                    </div>

                    {/* RIGHT SMALL BADGE */}

                    <div
                        className="
                            hidden
                            shrink-0
                            items-center
                            gap-3
                            rounded-full
                            border
                            border-[#D7A74A]/30
                            bg-white
                            px-5
                            py-3
                            shadow-[0_8px_25px_rgba(11,85,82,0.06)]

                            lg:flex
                        "
                    >
                        <div
                            className="
                                flex
                                h-9
                                w-9
                                items-center
                                justify-center
                                rounded-full
                                bg-[#8B0000]
                                text-white
                            "
                        >
                            ✓
                        </div>

                        <div>
                            <p
                                className="
                                    text-[11px]
                                    font-bold
                                    text-[#0B5552]
                                "
                            >
                                Trusted Hotel Booking
                            </p>

                            <p
                                className="
                                    mt-0.5
                                    text-[9px]
                                    text-[#71808A]
                                "
                            >
                                Simple • Secure • Reliable
                            </p>
                        </div>
                    </div>
                </div>


                {/* =====================================================
                    BENEFIT CARDS
                ===================================================== */}

                <div
                    className="
                        grid
                        grid-cols-1
                        gap-4

                        sm:grid-cols-2

                        lg:grid-cols-4
                        lg:gap-5
                    "
                >

                    {benefits.map((benefit) => {
                        const Icon = benefit.icon;

                        return (
                            <div
                                key={benefit.title}
                                className="
                                    group
                                    relative
                                    overflow-hidden
                                    rounded-[24px]
                                    border
                                    border-[#E2E9EE]
                                    bg-white
                                    p-6

                                    shadow-[0_8px_30px_rgba(11,85,82,0.06)]

                                    transition-all
                                    duration-500

                                    hover:-translate-y-1.5
                                    hover:border-[#D7A74A]/40
                                    hover:shadow-[0_18px_40px_rgba(11,85,82,0.11)]

                                    sm:p-7
                                "
                            >

                                {/* =================================================
                                    TOP NUMBER
                                ================================================= */}

                                <div
                                    className="
                                        absolute
                                        right-5
                                        top-5
                                        text-[10px]
                                        font-bold
                                        tracking-[0.15em]
                                        text-[#D7A74A]
                                    "
                                >
                                    {benefit.number}
                                </div>


                                {/* =================================================
                                    ICON
                                ================================================= */}

                                <div
                                    className="
                                        relative
                                        mb-6
                                        flex
                                        h-14
                                        w-14
                                        items-center
                                        justify-center
                                        rounded-2xl
                                        bg-[#F8F5EF]

                                        transition-all
                                        duration-500

                                        group-hover:bg-[#8B0000]
                                    "
                                >

                                    {/* Gold corner */}

                                    <span
                                        className="
                                            absolute
                                            right-0
                                            top-0
                                            h-2
                                            w-2
                                            rounded-tr-2xl
                                            bg-[#D7A74A]
                                            transition-all
                                            duration-300
                                            group-hover:bg-[#D7A74A]
                                        "
                                    />

                                    <Icon
                                        className="
                                            h-7
                                            w-7
                                            text-[#0B5552]
                                            transition-all
                                            duration-500
                                            group-hover:text-white
                                        "
                                        strokeWidth={1.5}
                                    />
                                </div>


                                {/* =================================================
                                    TITLE
                                ================================================= */}

                                <h3
                                    className="
                                        max-w-[220px]
                                        font-serif
                                        text-[18px]
                                        font-medium
                                        leading-tight
                                        text-[#0B5552]

                                        transition-colors
                                        duration-300

                                        group-hover:text-[#8B0000]
                                    "
                                >
                                    {benefit.title}
                                </h3>


                                {/* =================================================
                                    DESCRIPTION
                                ================================================= */}

                                <p
                                    className="
                                        mt-3
                                        text-[11px]
                                        leading-5
                                        text-[#71808A]

                                        sm:text-[12px]
                                        sm:leading-6
                                    "
                                >
                                    {benefit.description}
                                </p>


                                {/* =================================================
                                    BOTTOM LINE
                                ================================================= */}

                                <div
                                    className="
                                        mt-6
                                        h-px
                                        w-10
                                        bg-[#D7A74A]

                                        transition-all
                                        duration-500

                                        group-hover:w-16
                                        group-hover:bg-[#8B0000]
                                    "
                                />


                                {/* =================================================
                                    DECORATIVE CIRCLE
                                ================================================= */}

                                <div
                                    className="
                                        pointer-events-none
                                        absolute
                                        -bottom-16
                                        -right-16
                                        h-32
                                        w-32
                                        rounded-full
                                        border
                                        border-[#D7A74A]/10

                                        transition-all
                                        duration-700

                                        group-hover:scale-150
                                    "
                                />

                            </div>
                        );
                    })}

                </div>


                {/* =====================================================
                    BOTTOM TRUST MESSAGE
                ===================================================== */}

                <div
                    className="
                        mt-8
                        flex
                        flex-col
                        items-center
                        justify-center
                        gap-2
                        text-center

                        sm:flex-row
                        sm:gap-3
                    "
                >

                    <div className="flex items-center gap-1">

                        <span className="text-[#D7A74A]">★</span>
                        <span className="text-[#D7A74A]">★</span>
                        <span className="text-[#D7A74A]">★</span>
                        <span className="text-[#D7A74A]">★</span>
                        <span className="text-[#D7A74A]">★</span>

                    </div>

                    <span
                        className="
                            text-[10px]
                            font-medium
                            text-[#71808A]
                        "
                    >
                        Trusted by travelers booking stays across Dubai
                    </span>

                </div>

            </div>
        </section>
    );
}